import { zodResolver } from "@hookform/resolvers/zod";
import { captureException } from "@sentry/react";
import { OutOfAreaActivationAvailabilityStatus } from "@ses-mams/api-contract";
import {
  formatDateForServer,
  getUpdatedPagedData,
  useUpdateEffect,
} from "@ses-mams/react-utils";
import {
  OutOfAreaActivationApprovalFormSchema,
  outOfAreaActivationApprovalFormSchema,
} from "@ses-mams/validation";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button } from "~/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerProps,
} from "~/components/ui/drawer";
import { TextField } from "~/components/ui/textField";
import { useToast } from "~/components/ui/toast";
import { tsr } from "~/utils/client";
import { useShowConflictConfirmationDialog } from "~/hooks/useShowConflictConfirmationDialog";

type ApprovalParams = {
  requestIds: Array<string>;
  roleIds?: Array<string>;
  availabilityBlocks?: Array<{
    start: Date;
    end: Date;
    availabilityStatus: OutOfAreaActivationAvailabilityStatus;
    conditionalReason?: string;
  }>;
  activationId: string;
};

type ActivateCommentsDrawerProps = Omit<DrawerProps, "children"> &
  ApprovalParams & {
    onSuccess?: () => void;
  };

export const ActivateCommentsDrawer = ({
  requestIds,
  roleIds,
  availabilityBlocks,
  open,
  onClose,
  onSuccess,
  activationId,
}: ActivateCommentsDrawerProps) => {
  const { formState, register, reset, handleSubmit } =
    useForm<OutOfAreaActivationApprovalFormSchema>({
      resolver: zodResolver(outOfAreaActivationApprovalFormSchema),
      mode: "onBlur",
    });

  useUpdateEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  const { activate, isPending } = useActivateWithChanges({
    requestIds,
    roleIds,
    availabilityBlocks,
    onSuccess: useCallback(() => {
      onClose();
      onSuccess?.();
    }, [onSuccess, onClose]),
    activationId,
  });

  return (
    <Drawer open={open} onClose={onClose}>
      <DrawerHeader onClose={onClose}>
        Activation Comment (optional)
      </DrawerHeader>
      <form
        onSubmit={handleSubmit(activate)}
        style={{ display: "flex", flexDirection: "column", height: "100%" }}
      >
        <DrawerContent>
          <TextField
            {...register("comments")}
            label="Comments"
            hideLabel
            multiline
            rows={4}
            placeholder="Enter comments"
            errorMessage={formState.errors?.comments?.message}
          />
        </DrawerContent>

        <DrawerFooter>
          <Button type="submit" fullWidth busy={isPending}>
            Done
          </Button>
        </DrawerFooter>
      </form>
    </Drawer>
  );
};

type Params = ApprovalParams & {
  onSuccess: () => void;
};

const useActivateWithChanges = ({
  requestIds,
  roleIds,
  availabilityBlocks,
  onSuccess,
  activationId,
}: Params) => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const queryClient = tsr.useQueryClient();

  const { showConflictConfirmationDialog } = useShowConflictConfirmationDialog({
    type: "activation",
  });

  const { isPending, mutateAsync } =
    tsr.outOfAreaActivations.activate.useMutation();

  const activate = useCallback(
    async ({
      comments,
      force,
    }: {
      comments?: string | undefined;
      force?: boolean;
    }) => {
      try {
        const result = await mutateAsync({
          params: {
            activationId,
          },
          body: {
            requestIds,
            comments,
            force,
            overrideRoleIds: roleIds
              ? roleIds.filter(id => id.trim().length > 0)
              : undefined,
            overrideAvailabilityBlocks: availabilityBlocks
              ? availabilityBlocks.map(block => ({
                  ...block,
                  start: formatDateForServer(block.start),
                  end: formatDateForServer(block.end),
                }))
              : undefined,
          },
        });

        queryClient.setQueriesData(
          {
            queryKey: [
              "out-of-area-activation",
              { activationId },
              "members-v2",
            ],
          },
          getUpdatedPagedData(result.body)
        );

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [
              "out-of-area-activation-requests",
              "details",
              { activationId },
            ],
            exact: true,
          }),
          queryClient.invalidateQueries({
            queryKey: ["out-of-area-activation-requests", "list"],
          }),
        ]);

        navigate(`/requests/out-of-area-activations/${activationId}`);

        onSuccess();
      } catch (error) {
        const apiError = error as any;
        if (apiError?.status === 422) {
          showConflictConfirmationDialog({
            text: apiError.body.message,
            onConfirm: () => activate({ comments, force: true }),
          });
          return;
        }

        captureException(error);

        addToast({
          tone: "critical",
          title: "Sorry, something went wrong",
          message: "Please try again",
        });
      }
    },
    [requestIds, roleIds, availabilityBlocks, onSuccess, activationId]
  );

  return {
    isPending,
    activate,
  };
};
